/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { Image, Table } from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/StudentCourseDrivenAssignmentsTable.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { CONTENT_ITEM_TYPES } from '../managers/ContentManager';
import { DIALOG_NAMES } from '../managers/DialogManager';

import ImageService from '../services/ImageService';
import AssignmentService from '../services/AssignmentService';

import defaultCourseImage from '../img/default-course.svg';
import defaultAssignmentImage from '../img/default-assessment.svg';

import iconCircledCheckmark from '../img/icon-circled-checkmark.png';

import PopupService from '../services/PopupService';
import UtilityService from '../services/UtilityService';

import { getFeedbackIcon, getStyleVar, stripHtmlTags } from '../utils';

const StudentCourseDrivenAssignmentsTable = observer((props = {}) => {
  const { t } = props;

  const {
    assignmentManager,
    classroomManager,
    courseManager,
    dialogManager,
    studentCourseDrivenExperienceManager
  } = useContext(MobXProviderContext);

  const submittedImg = iconCircledCheckmark;
  const submittedImgLabel = t('submittedImgLabel');

  const filteredAssignments = props.filteredAssignments || assignmentManager.assignmentArray;

  const hasFilteredAssignments = !!filteredAssignments?.length;

  const FeedbackIcon = SatCoreComponent('FeedbackIcon');
  const KeyboardActionListener = SatCoreComponent('KeyboardActionListener');
  const NoAssignmentsMessage = SatCoreComponent('NoAssignmentsMessage');

  const {
    allowStudentCourseDrivenAssignmentFiltering,
    selectedCourseDrivenCarouselIndex
  } = studentCourseDrivenExperienceManager;

  const courseList = courseManager.getCourseList(classroomManager.currentClassroomId);
  const selectedCourse = courseList[selectedCourseDrivenCarouselIndex];

  const handleFeedback = async (activityInstanceId) => {
    dialogManager.setOpenDialog(DIALOG_NAMES.STUDENT_FEEDBACK, {
      assignmentInstanceId: activityInstanceId
    }, () => dialogManager.closeDialog(DIALOG_NAMES.STUDENT_FEEDBACK));
  };

  const handleOpenStudentAssignmentModal = (_event, { assignment } = {}) => {
    if (!assignment) {
      return;
    }
    dialogManager.setOpenDialog(DIALOG_NAMES.STUDENT_ASSIGNMENT, {
      assignmentArray: [assignment],
      contentName: assignment.displayName || assignment.name,
      courseElement: {},
      fromStudentCourseDrivenAssignments: true,
      handlePresent: props.handlePresent,
      hasMore: false,
      loadMore: () => {},
      menuStatus: studentCourseDrivenExperienceManager.selectedCourseDrivenAssignmentMenuStatus,
    }, () => {
      dialogManager.closeAllDialogs();
    });
  };

  const getTableRowImage = (imageUrl, defaultImage, {
    additionalClassNames,
    altText
  } = {}) => {
    return (
      <div className={classNames('content-type-img-wrapper', additionalClassNames, {
        default: !imageUrl && !!defaultImage
      })}>
        <Image alt={altText} className={classNames('content-type-img', additionalClassNames, {
          default: !imageUrl && !!defaultImage
        })} src={imageUrl || defaultImage} />
      </div>
    );
  };

  const renderAssignmentsTable = () => {
    return (
      <>
        {hasFilteredAssignments && (
          <Table className='student-progress-table student-course-driven-assignments-table' unstackable>
            <Table.Header>
              {renderAssignmentHeaderRow()}
              {!!allowStudentCourseDrivenAssignmentFiltering && renderCourseHeaderRow()}
            </Table.Header>
            <Table.Body>
              {filteredAssignments.map((assignment) => renderAssignmentBodyRow(assignment))}
            </Table.Body>
          </Table>
        )}
        {!hasFilteredAssignments && <NoAssignmentsMessage message={t('NoAssignmentsYet')} />}
      </>
    );
  };

  const renderAssignmentHeaderRow = () => {
    return (
      <Table.Row className='student-progress-header-row student-course-driven-assignments-header-row'>
        {renderHeaderRowPlaceholderCell()}
        {renderHeaderRowContentTitleCell()}
        {renderHeaderRowScoreCell()}
        {renderHeaderRowSubmittedCell()}
        {renderHeaderRowFeedbackCell()}
        {renderHeaderRowPlaceholderCell()}
      </Table.Row>
    );
  };

  /** ASSIGNMENT HEADER CELL: PLACEHOLDER */
  const renderHeaderRowPlaceholderCell = () => {
    return (
      <Table.HeaderCell className='student-progress-placeholder student-course-driven-assignments-cell-placeholder' />
    );
  };

  /** ASSIGNMENT HEADER CELL: CONTENT (TITLE) */
  const renderHeaderRowContentTitleCell = () => {
    return (
      <Table.HeaderCell
        className='student-progress-content student-course-driven-assignments-cell-content'
        scope='col'>
        <span>{t('content')}</span>
      </Table.HeaderCell>
    );
  };

  /** ASSIGNMENT HEADER CELL: SCORE */
  const renderHeaderRowScoreCell = () => {
    return (
      <Table.HeaderCell
        className='student-progress-score student-course-driven-assignments-cell-score'
        scope='col'
        textAlign='center'>
        <span>{t('score')}</span>
      </Table.HeaderCell>
    );
  };

  /** ASSIGNMENT HEADER CELL: SUBMITTED */
  const renderHeaderRowSubmittedCell = () => {
    return (
      <Table.HeaderCell
        className='student-progress-submitted student-course-driven-assignments-cell-submitted'
        scope='col'
        textAlign='center'>
        <span>{t('submitted')}</span>
      </Table.HeaderCell>
    );
  };

  /** ASSIGNMENT HEADER CELL: FEEDBACK */
  const renderHeaderRowFeedbackCell = () => {
    return (
      <Table.HeaderCell
        className='student-progress-feedback student-course-driven-assignments-cell-feedback'
        scope='col'
        textAlign='center'>
        <span>{t('feedback')}</span>
      </Table.HeaderCell>
    );
  };

  /** COURSE HEADER ROW */
  const renderCourseHeaderRow = () => {
    if (!selectedCourse) {
      return null;
    }

    const courseImageUrl = CONTENT_ITEM_TYPES.getContentImageUrl(selectedCourse.imageUrl);

    const courseNameObj = UtilityService.reactHtmlParserWrapper(
      selectedCourse.courseName || selectedCourse.name || t('untitledCourse')
    );

    return (
      <Table.Row className='student-progress-course-header-row course-header-row' tabIndex={0}>
        {renderHeaderRowPlaceholderCell()}
        <Table.HeaderCell
          className='student-progress-content student-course-driven-assignments-cell-content'
          colSpan={5}
          scope='col'>
          <div className='assignment-content student-course-driven-assignments-cell-content-title-wrapper'>
            {getTableRowImage(courseImageUrl, defaultCourseImage, {
              additionalClassNames: 'course',
              altText: selectedCourse?.altText || t('defaultCourseImageAlt')
            })}
            {PopupService.renderPopup({
              content: courseNameObj?.parsed,
              position: 'top left',
              trigger: (
                <div className='assignment-content-title student-course-driven-assignments-cell-content-title' tabIndex={0}>
                  {courseNameObj?.stripped}
                </div>
              ),
              wide: false
            })}
          </div>
        </Table.HeaderCell>
        {renderHeaderRowPlaceholderCell()}
      </Table.Row>
    );
  };

  const renderAssignmentBodyRow = (assignment) => {
    return (
      <Table.Row key={assignment.id} tabIndex={0}>
        {renderRowPlaceholderCell(assignment)}
        {renderRowContentTitleCell(assignment)}
        {renderRowScoreCell(assignment)}
        {renderRowSubmittedCell(assignment)}
        {renderRowFeedbackCell(assignment)}
        {renderRowPlaceholderCell(assignment)}
      </Table.Row>
    );
  };

  /** ASSIGNMENT BODY CELL: PLACEHOLDER */
  const renderRowPlaceholderCell = (_assignment) => {
    return (
      <Table.Cell className='student-progress-placeholder student-course-driven-assignments-cell-placeholder' />
    );
  };

  /** ASSIGNMENT BODY CELL: CONTENT (TITLE) */
  const renderRowContentTitleCell = (assignment) => {
    const contentImageUrl = ImageService.getImageUrl(assignment);

    const { resourceWebTitle } = AssignmentService.getAssignmentTitleAndDescription(assignment);

    const contentStringObj = UtilityService.reactHtmlParserWrapper(
      resourceWebTitle || t('untitledAssignment')
    );
    const courseName = stripHtmlTags(assignment?.courseContentItemName) || '';

    return (
      <Table.Cell
        className='student-progress-content student-course-driven-assignments-cell-content'
        onClick={(event) => handleOpenStudentAssignmentModal(event, { assignment })}
        scope='col'>
        <div className='assignment-content'>
          {getTableRowImage(contentImageUrl, defaultAssignmentImage, {
            additionalClassNames: 'assignment',
            altText: assignment?.altText || t('defaultAssignmentImageAlt')
          })}
          {PopupService.renderPopup({
            content: (
              <div className='assignment-content-title clickable'>
                {contentStringObj?.parsed}
                {!allowStudentCourseDrivenAssignmentFiltering && !!courseName && (
                  <p className='assignment-content-title-course'>
                    {t('assignmentContentTitleCourse', { courseName })}
                  </p>
                )}
              </div>
            ),
            position: 'top left',
            trigger: (
              <div className='assignment-content-title clickable'
                // onClick={(event) => handleOpenStudentAssignmentModal(event, { assignment })}
                tabIndex={0}>
                {contentStringObj?.stripped}
              </div>
            ),
            wide: false
          })}
        </div>
      </Table.Cell>
    );
  };

  /** ASSIGNMENT BODY CELL: SCORE */
  const renderRowScoreCell = (assignment) => {
    const gradeNum = parseFloat(assignment.grade) || 0;
    return (
      <Table.Cell
        className='student-progress-score student-course-driven-assignments-cell-score'
        scope='col'
        textAlign='center'>
        {`${gradeNum}%`}
      </Table.Cell>
    );
  };

  /** ASSIGNMENT BODY CELL: SUBMITTED */
  const renderRowSubmittedCell = (assignment) => {
    return (
      <Table.Cell
        className='student-progress-submitted student-course-driven-assignments-cell-submitted'
        scope='col'
        textAlign='center'>
        {!!assignment.submitted && (
          <div className='assignment-submitted-img-wrapper'>
            <Image alt={submittedImgLabel} aria-label={submittedImgLabel}
              className='assignment-submitted-img' src={submittedImg} />
          </div>
        )}
      </Table.Cell>
    );
  };

  /** ASSIGNMENT BODY CELL: FEEDBACK */
  const renderRowFeedbackCell = (assignment) => {
    const iconCommentActiveColor = getStyleVar('--icon-feedback-active-default-color');
    const iconCommentActive = getFeedbackIcon(iconCommentActiveColor);
    return (
      <Table.Cell
        className='student-progress-feedback student-course-driven-assignments-cell-feedback'
        scope='col'
        textAlign='center'>
        {(!!assignment?.teacherFeedback) && (
          <div className='assignment-feedback-icon-wrapper'>
            <FeedbackIcon
              className='assignment-feedback'
              clickHandler={() => handleFeedback(assignment.activityInstanceId)}
              hasFeedback={true}
              icon={iconCommentActive}
              tabIndex={0} />
          </div>
        )}
      </Table.Cell>
    );
  };

  return (
    <div className='StudentCourseDrivenAssignmentsTableContainer'>
      <KeyboardActionListener>
        <div className='assignments-widget student-course-driven-assignments-table-widget'>
          <div className={classNames('assignments-top-padding', {
            'assignments-top-padding-no-header': !hasFilteredAssignments
          })} />
          <div className='assignments-widget-inner student-course-driven-assignments-table-widget-inner'>
            {renderAssignmentsTable()}
          </div>
        </div>
      </KeyboardActionListener>
    </div>
  );
});

export default StudentCourseDrivenAssignmentsTable;

SatCoreRegister('StudentCourseDrivenAssignmentsTable', StudentCourseDrivenAssignmentsTable);
